import * as React from 'react';
import './LoginPage.css';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  LoginFooterItem,
  LoginMainFooterBandItem,
  LoginPage,
  ListVariant,
  EmptyState,
  Spinner,
  Level,
  LevelItem,
  Brand,
  Stack,
  StackItem,
  EmptyStateBody,
  Bullseye,
} from '@patternfly/react-core';
import ory from '@app/lib/OrySdk';
import { AuthContext } from '@app/lib/AuthProvider';

import { useTranslation } from 'react-i18next';

import launchLogo from '@app/bgimages/launch-logo.png';
import nsfLogo from '@app/bgimages/nsf-logo-60.png';

import { LoginFlow, UpdateLoginFlowBody } from '@ory/client';
import { UserAuthCard } from '@ory/elements';
import { sdkError } from '@app/lib/OrySdk';

const MergeLoginPage: React.FunctionComponent = () => {
  const [flow, setFlow] = React.useState<LoginFlow>();
  const history = useHistory();
  const { setSession, isAuthenticated, loading } = React.useContext(AuthContext);

  const { t } = useTranslation();

  // When loading, check to see if the user is authenticated.
  React.useEffect(() => {
    if (isAuthenticated) {
      history.replace('/');
    }
  }, [isAuthenticated, history]);

  const params = React.useMemo(() => {
    return new URLSearchParams(document.location.search);
  }, []);
  const aal2 = params.get('aal2');
  const loginChallenge = params.get('login_challenge');
  const returnTo = params.get('return_to');

  // initialize the sdkError for generic handling of errors
  const sdkErrorHandler = sdkError(undefined, setFlow, '/recovery');

  // Get the flow based on the flowId in the URL (.e.g redirect to this page after flow initialized)
  const getFlow = React.useCallback(
    (flowId: string) =>
      ory
        // the flow data contains the form fields, error messages and csrf token
        .getLoginFlow({ id: flowId })
        .then(({ data: flow }) => setFlow(flow))
        .catch(sdkErrorHandler),
    [sdkErrorHandler]
  );

  // Create a new login flow.
  const createFlow = React.useCallback(() => {
    return ory
      .createBrowserLoginFlow({
        refresh: true,
        aal: aal2 ? 'aal2' : 'aal1',
        ...(loginChallenge && { loginChallenge }),
        ...(returnTo && { returnTo }),
      })
      .then(({ data: flow }) => {
        const searchParams = new URLSearchParams(location.search);

        // Only push a new history entry if we weren't already on a flow.
        if (!searchParams.has('flow')) {
          searchParams.set('flow', flow.id);
          history.push({ pathname: location.pathname, search: searchParams.toString() });
        } 
        
        else {
          searchParams.set('flow', flow.id);
          history.replace({ pathname: location.pathname, search: searchParams.toString() });
        }

        setFlow(flow);
      })
      .catch(sdkErrorHandler);
  }, [aal2, loginChallenge, returnTo, history, sdkErrorHandler]);

  // submit the login form data to Ory
  const submitFlow = (body: UpdateLoginFlowBody) => {
    // something unexpected went wrong and the flow was not set
    if (!flow) return history.push('/login', { replace: true });

    // we submit the flow to Ory with the form data
    ory
      .updateLoginFlow({ flow: flow.id, updateLoginFlowBody: body })
      .then(({ data: session }) => {
        setSession(session.session);
      })
      .catch(sdkErrorHandler);
  };

  React.useEffect(() => {
    // Don't create flow if we're still loading auth state or if already authenticated.
    if (loading || isAuthenticated) {
      return;
    }

    // We might redirect to this page after the flow is initialized, so we check for the flowId in the URL.
    const flowId = params.get('flow');
    if (flowId) {
      // If for some reason the flow has expired, we need to get a new one.
      getFlow(flowId).catch(createFlow); 
      return;
    }

    // We assume there was no flow, so we create a new one.
    createFlow();
  }, [params, getFlow, createFlow, loading, isAuthenticated]);

  // Show loading while auth state is being determined.
  if (loading) {
    return (
      <EmptyState icon={Spinner} variant="full">
        <EmptyStateBody>Loading...</EmptyStateBody>
      </EmptyState>
    );
  }

  // TODO: Add Terms of Use and Privacy Policy
  const footerListItems = (
    <React.Fragment>
      <Stack hasGutter>
        <StackItem>
          <div className="beta-text">
            This is a beta version of the SPHERE infrastructure. It may become unstable and/or unreachable at times.{' '}
          </div>
        </StackItem>

        <StackItem>
          <Level hasGutter>
            <LevelItem>
              <LoginFooterItem href="https://mergetb.gitlab.io/testbeds/sphere/sphere-docs/docs/experimentation/">
                Documentation
              </LoginFooterItem>
            </LevelItem>
            <LevelItem>
              <LoginFooterItem href="https://mergetb.gitlab.io/testbeds/sphere/sphere-docs/docs/experimentation/hello-world-gui/">
                HOWTO
              </LoginFooterItem>
            </LevelItem>
            <LevelItem>
              <LoginFooterItem href="https://chat.mergetb.net/mergetb">Chat</LoginFooterItem>
            </LevelItem>
            <LevelItem>
              <LoginFooterItem href="https://gitlab.com/mergetb">Source Code</LoginFooterItem>
            </LevelItem>
          </Level>
        </StackItem>
        <StackItem isFilled></StackItem>
        <StackItem>
          <StackItem isFilled></StackItem>
          <StackItem>
            <a href="https://nsf.gov" target="_blank" rel="noreferrer">
              <Bullseye>
                <Brand src={nsfLogo} alt="NSF" />
                National Science Foundation
              </Bullseye>
            </a>
          </StackItem>
        </StackItem>
      </Stack>
    </React.Fragment>
  );

  const signUpForAccountMessage = (
    <LoginMainFooterBandItem>
      <Stack>
        <StackItem>
          Need a new account? <Link to="/registration">Register here.</Link>
        </StackItem>
      </Stack>
    </LoginMainFooterBandItem>
  );

  const forgotCredentials = (
    <LoginMainFooterBandItem>
      <Link to="/recovery">Forgot username or password?</Link>
    </LoginMainFooterBandItem>
  );

  // This fixes a centering issue with CSS on the login page. Do not apply this globally.
  // Otherwise, every page will break.
  const cssLogin = `
    .pf-v6-c-page.pf-m-resize-observer.pf-m-breakpoint-2xl {
      display: flex;
      justify-content: center;
      width: 100%;
      height: 100%;
    }
  `;

  return flow ? (
    <>
      <style>{cssLogin}</style>
      <div className="login-page-container">
        <LoginPage
          loginTitle=""
          footerListItems={footerListItems}
          footerListVariants={ListVariant.inline}
          brandImgSrc={launchLogo}
          brandImgAlt="Merge Launch"
          backgroundImgSrc="/images/bgimage.png"
          textContent={'Welcome to Launch, a portal to the Security and Privacy Heterogeneous Environment for Reproducible Experimentation (SPHERE) testbed clusters. Powered by Merge Testbed technology.'}
          signUpForAccountMessage={signUpForAccountMessage}
          forgotCredentials={forgotCredentials}
        >
          {flow ? (
            <UserAuthCard
              title={t('login_message')}
              flow={flow}
              flowType={'login'}
              includeScripts={false}
              onSubmit={({ body }) => submitFlow(body as UpdateLoginFlowBody)}
            />
          ) : (
            <Spinner />
          )}
        </LoginPage>
      </div>
    </>
  ) : (
    <EmptyState icon={Spinner} variant="full">
      <EmptyStateBody>Contacting Authorization Server</EmptyStateBody>
    </EmptyState>
  );
};

export { MergeLoginPage };